import { useCallback, useMemo, useState } from "react";
import { NativeInvoiceDraftBody } from "@common/schemas/domains/invoice/native-invoice.schema";
import { useUpdateInvoiceTemplateContent } from "@api/invoiceTemplate.api";
import type { NativeInvoiceLineCodingByLineId } from "./nativeInvoiceFormTypes";
import { toInvoiceTemplateContentBody } from "./toInvoiceTemplateContentBody";
import { SaveResult, SaveStatus } from "./useNativeInvoiceSave";

interface Params {
    workspaceId: string | undefined;
    entityId: number | undefined;
    templateId: string | undefined;
    draft: NativeInvoiceDraftBody | null;
    draftLineCodingBaseline?: NativeInvoiceLineCodingByLineId;
    dirtyFingerprint: string;
    onSaveSuccess?: (meta: {
        savedLineCodingBaseline: NativeInvoiceLineCodingByLineId;
    }) => void;
}

function hashDraft(draft: NativeInvoiceDraftBody | null): string {
    if (!draft) {
        return "";
    }
    return JSON.stringify(toInvoiceTemplateContentBody(draft));
}

export function useInvoiceTemplateContentSave({
    workspaceId,
    entityId,
    templateId,
    draft,
    draftLineCodingBaseline,
    dirtyFingerprint,
    onSaveSuccess,
}: Params): SaveResult {
    const updateMutation = useUpdateInvoiceTemplateContent();
    const [status, setStatus] = useState<SaveStatus>("idle");
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
    const [lastSavedHash, setLastSavedHash] = useState("");
    const [baselineFingerprint, setBaselineFingerprint] =
        useState(dirtyFingerprint);
    const [inFlight, setInFlight] = useState<Promise<
        string | undefined
    > | null>(null);

    const draftHash = useMemo(() => hashDraft(draft), [draft]);
    const updateAsync = updateMutation.mutateAsync;

    const save = useCallback(async (): Promise<string | undefined> => {
        if (inFlight) {
            return await inFlight;
        }
        if (
            !workspaceId ||
            entityId === undefined ||
            !templateId ||
            !draft ||
            draftHash === lastSavedHash
        ) {
            return templateId;
        }

        const savedLineCodingBaseline = draftLineCodingBaseline ?? {};
        const savedFingerprint = dirtyFingerprint;
        const request = updateAsync({
            entityId,
            templateId,
            body: toInvoiceTemplateContentBody(draft),
        }).then(() => {
            setLastSavedHash(draftHash);
            setLastSavedAt(new Date());
            setBaselineFingerprint(savedFingerprint);
            setStatus("saved");
            onSaveSuccess?.({ savedLineCodingBaseline });
            return templateId;
        });

        setStatus("saving");
        setInFlight(request);
        try {
            return await request;
        } catch (error) {
            setStatus("error");
            throw error;
        } finally {
            setInFlight(null);
        }
    }, [
        inFlight,
        workspaceId,
        entityId,
        templateId,
        draft,
        draftHash,
        lastSavedHash,
        draftLineCodingBaseline,
        dirtyFingerprint,
        updateAsync,
        onSaveSuccess,
    ]);

    return {
        invoiceId: templateId,
        status,
        lastSavedAt,
        isDirty: baselineFingerprint !== dirtyFingerprint,
        acknowledgeEntityRescope: () => undefined,
        rebaseCleanBaselineAfterAddress: () => undefined,
        rebaseBaselineAfterTemplateApply: () => undefined,
        save,
    };
}
